
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUp, icons } from 'lucide-react';
import { useContactDetails } from '@/hooks/useContactDetails';
import { handleAnchorLink } from '@/lib/scrollUtils';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { getEmails, getPhones, getAddresses } = useContactDetails('footer');
  const navigate = useNavigate();
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <footer className="bg-slate-900 text-white relative">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4">
                GoalTec
              </h3>
              <p className="text-slate-300 text-lg leading-relaxed max-w-md">
                Passionate IT service provider with extensive experience in software development,
                web applications, and digital transformation solutions.
              </p>
            </div>
            
            <div className="space-y-4">
              {getEmails().map((email) => {
                let IconComponent = email.icon_name && icons[email.icon_name as keyof typeof icons] 
                  ? icons[email.icon_name as keyof typeof icons] 
                  : Mail;
                
                return (
                  <div key={email.id} className="flex items-center space-x-3 text-slate-300">
                    {email.custom_icon_url ? (
                      <img 
                        src={email.custom_icon_url} 
                        alt={`${email.label} icon`}
                        className="w-5 h-5"
                      />
                    ) : (
                      <IconComponent size={20} className="text-blue-400" />
                    )}
                    <a 
                      href={`mailto:${email.value}`} 
                      className="hover:text-white transition-colors"
                    >
                      {email.value}
                    </a>
                  </div>
                );
              })}
              
              {getPhones().map((phone) => {
                let IconComponent = phone.icon_name && icons[phone.icon_name as keyof typeof icons] 
                  ? icons[phone.icon_name as keyof typeof icons] 
                  : Phone;
                
                return (
                  <div key={phone.id} className="flex items-center space-x-3 text-slate-300">
                    {phone.custom_icon_url ? (
                      <img 
                        src={phone.custom_icon_url} 
                        alt={`${phone.label} icon`}
                        className="w-5 h-5"
                      />
                    ) : (
                      <IconComponent size={20} className="text-blue-400" />
                    )}
                    <span>{phone.value}</span>
                  </div>
                );
              })}
              
              {getAddresses().map((address) => {
                let IconComponent = address.icon_name && icons[address.icon_name as keyof typeof icons] 
                  ? icons[address.icon_name as keyof typeof icons] 
                  : MapPin;
                
                return (
                  <div key={address.id} className="flex items-center space-x-3 text-slate-300">
                    {address.custom_icon_url ? (
                      <img 
                        src={address.custom_icon_url} 
                        alt={`${address.label} icon`}
                        className="w-5 h-5"
                      />
                    ) : (
                      <IconComponent size={20} className="text-blue-400" />
                    )}
                    <span>{address.value}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3">
              <FooterLink to="/" navigate={navigate}>Home</FooterLink>
              <FooterLink to="/services" navigate={navigate}>Services</FooterLink>
              <FooterLink to="/projects" navigate={navigate}>Projects</FooterLink>
              <FooterLink to="/clients" navigate={navigate}>Clients</FooterLink>
              <FooterLink to="/about" navigate={navigate}>About Us</FooterLink>
              <FooterLink to="/contact" navigate={navigate}>Contact</FooterLink>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-white">Our Services</h3>
            <ul className="space-y-3">
              <FooterLink to="/services#web-development" navigate={navigate}>Web Development</FooterLink>
              <FooterLink to="/services#software-solutions" navigate={navigate}>Software Solutions</FooterLink>
              <FooterLink to="/services#data-services" navigate={navigate}>Data Services</FooterLink>
              <FooterLink to="/services#cloud-computing" navigate={navigate}>Cloud Computing</FooterLink>
              <FooterLink to="/services#ai-services" navigate={navigate}>AI Solutions</FooterLink>
            </ul>
          </div>
        </div>

        <div className="section-divider my-12"></div>

        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-slate-400 mb-4 md:mb-0">
            &copy; {currentYear} GoalTec. All rights reserved.
          </p>
          
          <button 
            onClick={scrollToTop}
            className="bg-primary hover:bg-primary/90 text-white p-3 rounded-xl transition-all duration-300 hover:-translate-y-1 shadow-lg"
          >
            <ArrowUp size={20} />
          </button>
        </div>
      </div>
    </footer>
  );
};

const FooterLink = ({ to, children, navigate }: { to: string; children: React.ReactNode; navigate: (path: string) => void }) => (
  <li>
    <button
      onClick={(e) => {
        e.preventDefault();
        handleAnchorLink(to, navigate);
      }}
      className="text-slate-300 hover:text-white transition-colors duration-300 hover:underline text-left"
    >
      {children}
    </button>
  </li>
);

export default Footer;
